import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import PageHeader, { KpiTile } from '../components/common/PageHeader';
import ResponsiveTable from '../components/common/ResponsiveTable';
import { NativeSelect, TextArea } from '../components/common/FormField';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import { useDebounce } from '../hooks/useDebounce';
import { queryKeys } from '../lib/queryClient';
import { api, errorMessage, triggerBlobDownload } from '../services/api';
import { Employee, Expense, ExpenseCategory, ExpenseList } from '../types';
import { compact, focusFirstError, hasErrors, serverFieldErrors, v } from '../utils/validation';
import {
  currentMonth,
  financialYear,
  fmtCurrency,
  fmtDate,
  fmtMonth,
  fyBounds,
  monthBounds,
  PAYMENT_MODES,
  paymentModeLabel,
  todayISO,
  toNum,
} from '../utils/format';

interface FormState {
  expenseDate: string;
  categoryId: string;
  payee: string;
  description: string;
  amount: string;
  taxAmount: string;
  paymentMode: string;
  referenceNo: string;
  billNumber: string;
  employeeId: string;
  notes: string;
}

const emptyForm = (): FormState => ({
  expenseDate: todayISO(), categoryId: '', payee: '', description: '', amount: '', taxAmount: '',
  paymentMode: 'bank_transfer', referenceNo: '', billNumber: '', employeeId: '', notes: '',
});

export function CategoryChip({ category }: { category?: ExpenseCategory }) {
  if (!category) return <span>—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-ink-800">
      <span className="w-6 h-6 rounded-md flex items-center justify-center text-[11px] shrink-0"
        style={{ backgroundColor: `${category.color}1f`, color: category.color }}>
        <i className={`fas ${category.icon}`} />
      </span>
      {category.name}
    </span>
  );
}

export default function ExpensesPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { confirm, ConfirmDialog } = useConfirm();
  const canManage = hasPermission('expenses:manage');
  const canExport = hasPermission('reports:view');

  // Period: a month ('2026-10') or a whole financial year ('fy:2026-27')
  const [period, setPeriod] = useState(currentMonth());
  const [categoryId, setCategoryId] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [exporting, setExporting] = useState(false);

  const isFy = period.startsWith('fy:');
  const range = isFy ? fyBounds(period.slice(3)) : monthBounds(period);
  const filters = { from: range.from, to: range.to, categoryId: categoryId || undefined, search: debouncedSearch || undefined };

  const { data: categories = [] } = useQuery({
    queryKey: queryKeys.categories(),
    queryFn: () => api.get<ExpenseCategory[]>('/expense-categories'),
  });
  const { data: employees = [] } = useQuery({
    queryKey: queryKeys.employees.all(),
    queryFn: () => api.get<Employee[]>('/employees'),
    enabled: hasPermission('employees:view'),
  });
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.expenses.list(filters),
    queryFn: () => api.get<ExpenseList>('/expenses', filters),
  });
  const items = data?.items ?? [];
  const totals = data?.totals;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all() });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['reports'] });
  };
  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      editing ? api.patch(`/expenses/${editing.id}`, payload) : api.post('/expenses', payload),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/expenses/${id}`),
    onSuccess: invalidate,
  });

  function open(expense?: Expense) {
    setEditing(expense ?? null);
    setErrors({});
    setForm(
      expense
        ? {
            expenseDate: expense.expenseDate,
            categoryId: String(expense.categoryId),
            payee: expense.payee,
            description: expense.description ?? '',
            amount: String(parseFloat(expense.amount)),
            taxAmount: parseFloat(expense.taxAmount) ? String(parseFloat(expense.taxAmount)) : '',
            paymentMode: expense.paymentMode,
            referenceNo: expense.referenceNo ?? '',
            billNumber: expense.billNumber ?? '',
            employeeId: expense.employeeId ? String(expense.employeeId) : '',
            notes: expense.notes ?? '',
          }
        : emptyForm(),
    );
    setShowModal(true);
  }

  function setField(key: keyof FormState, value: string) {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => (p[key] ? { ...p, [key]: undefined } : p));
  }

  function validate(): boolean {
    const e = compact({
      expenseDate: v.date(form.expenseDate, 'Date', { notFuture: true }),
      categoryId: form.categoryId ? undefined : 'Category is required',
      payee: v.businessName(form.payee, 'Paid To', true, 150),
      description: v.text(form.description, 'Description', { max: 500 }),
      amount: v.money(form.amount, 'Amount'),
      taxAmount: v.money(form.taxAmount, 'GST', { required: false, allowZero: true }) ?? v.taxWithinLimit(form.amount, form.taxAmount),
      paymentMode: form.paymentMode ? undefined : 'Payment mode is required',
      referenceNo: v.reference(form.referenceNo),
      billNumber: v.reference(form.billNumber, 'Bill number'),
      notes: v.text(form.notes, 'Notes', { max: 1000 }),
    });
    setErrors(e);
    if (hasErrors(e)) focusFirstError();
    return !hasErrors(e);
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    try {
      await saveMutation.mutateAsync({
        expenseDate: form.expenseDate,
        categoryId: Number(form.categoryId),
        payee: form.payee.trim(),
        description: form.description.trim(),
        amount: toNum(form.amount),
        taxAmount: toNum(form.taxAmount) ?? 0,
        paymentMode: form.paymentMode,
        referenceNo: form.referenceNo.trim(),
        billNumber: form.billNumber.trim(),
        employeeId: form.employeeId ? Number(form.employeeId) : null,
        notes: form.notes.trim(),
      });
      showToast(editing ? 'Expense updated' : 'Expense recorded', 'success');
      setShowModal(false);
    } catch (err) {
      setErrors(serverFieldErrors(err, Object.keys(form) as (keyof FormState)[]));
      showToast(errorMessage(err, 'Failed to save expense'), 'error');
    }
  }

  async function handleDelete(expense: Expense) {
    const yes = await confirm({
      title: 'Delete Expense',
      message: `Delete ${fmtCurrency(expense.totalAmount)} paid to ${expense.payee} on ${fmtDate(expense.expenseDate)}?${
        expense.payrollMonth ? ' This is a payroll entry — the employee will show as unpaid for that month.' : ''
      }`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!yes) return;
    try {
      await deleteMutation.mutateAsync(expense.id);
      showToast('Expense deleted', 'success');
    } catch (err) {
      showToast(errorMessage(err, 'Failed to delete'), 'error');
    }
  }

  async function exportExcel() {
    setExporting(true);
    try {
      const blob = await api.download('/reports/export/expenses', { from: range.from, to: range.to });
      triggerBlobDownload(blob, `expenses_${range.from}_${range.to}.xlsx`);
    } catch (err) {
      showToast(errorMessage(err, 'Export failed'), 'error');
    } finally {
      setExporting(false);
    }
  }

  // Last 12 months + current & previous FY
  const periodOptions = [
    ...Array.from({ length: 12 }, (_, i) => {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - i);
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      return { value: m, label: fmtMonth(m) };
    }),
    { value: `fy:${financialYear()}`, label: `Full year — FY ${financialYear()}` },
  ];

  const columns = [
    { key: 'expenseDate', label: 'Date', width: '110px', render: (_: any, r: Expense) => <span className="text-sm text-ink-700">{fmtDate(r.expenseDate)}</span> },
    { key: 'category', label: 'Category', render: (_: any, r: Expense) => <CategoryChip category={r.category} /> },
    {
      key: 'payee',
      label: 'Paid To',
      render: (_: any, r: Expense) => (
        <div>
          <span className="text-sm font-semibold text-ink-900">{r.payee}</span>
          {r.description && <span className="block text-xs text-ink-500">{r.description}</span>}
          {r.recurringExpenseId && <span className="text-[10px] font-bold uppercase text-brand-600"><i className="fas fa-rotate mr-1" />Recurring</span>}
        </div>
      ),
    },
    {
      key: 'paymentMode',
      label: 'Paid Via',
      render: (_: any, r: Expense) => (
        <div className="text-xs text-ink-600">
          {paymentModeLabel(r.paymentMode)}
          {r.referenceNo && <span className="block font-mono text-ink-400">{r.referenceNo}</span>}
        </div>
      ),
    },
    {
      key: 'totalAmount',
      label: 'Amount',
      render: (_: any, r: Expense) => (
        <div>
          <span className="text-sm font-semibold text-ink-900">{fmtCurrency(r.totalAmount)}</span>
          {parseFloat(r.taxAmount) > 0 && <span className="block text-[11px] text-ink-400">incl. GST {fmtCurrency(r.taxAmount)}</span>}
        </div>
      ),
    },
    ...(canManage
      ? [{
          key: 'actions',
          label: 'Actions',
          width: '150px',
          render: (_: any, r: Expense) => (
            <div className="flex items-center gap-2">
              <button type="button" className="px-3 py-1 bg-ink-100 text-ink-700 text-xs rounded hover:bg-ink-200" onClick={() => open(r)}>
                <i className="fas fa-pen mr-1" />Edit
              </button>
              <button type="button" className="px-3 py-1 bg-red-50 text-red-600 text-xs rounded hover:bg-red-100" onClick={() => handleDelete(r)}>
                <i className="fas fa-trash-can" />
              </button>
            </div>
          ),
        }]
      : []),
  ];

  const total = (toNum(form.amount) ?? 0) + (toNum(form.taxAmount) ?? 0);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title="Expenses"
          subtitle="Everything the company spends — salaries, allowances, tools, internet, rent and more"
          actions={
            <>
              {canExport && (
                <Button variant="outline" icon={exporting ? 'fa-spinner fa-spin' : 'fa-file-excel'} onClick={exportExcel} disabled={exporting}>
                  Export
                </Button>
              )}
              {canManage && <Button variant="primary" icon="fa-plus" onClick={() => open()}>Add Expense</Button>}
            </>
          }
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 lg:mb-6">
          <KpiTile label="Total Spent" value={fmtCurrency(totals?.totalAmount ?? 0, 0)} icon="fa-wallet" tone="red"
            hint={isFy ? `FY ${period.slice(3)}` : fmtMonth(period)} />
          <KpiTile label="GST Paid (Input)" value={fmtCurrency(totals?.taxAmount ?? 0, 0)} icon="fa-percent" tone="violet" />
          <KpiTile label="Entries" value={totals?.count ?? 0} icon="fa-list" tone="ink" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 lg:mb-6">
          <NativeSelect value={period} onChange={setPeriod} options={periodOptions} />
          <NativeSelect value={categoryId} onChange={setCategoryId} placeholder="All Categories"
            options={categories.map((c) => ({ value: c.id, label: c.name }))} />
          <Input icon="fa-search" placeholder="Search payee, description, reference…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-ink-200 overflow-visible lg:overflow-hidden">
          <div className="px-1 py-3 lg:p-0">
            <ResponsiveTable columns={columns} data={items} isLoading={isLoading} height="calc(100vh - 400px)" emptyMessage="No expenses in this period" />
          </div>
        </div>

        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editing ? 'Edit Expense' : 'Add Expense'}
          size="lg"
          footer={
            <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2">
              <span className="text-sm text-ink-600">Total: <span className="font-bold text-ink-900">{fmtCurrency(total)}</span></span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleSubmit as any} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Date" type="date" required value={form.expenseDate} max={todayISO()}
                onChange={(e) => setField('expenseDate', e.target.value)} error={errors.expenseDate} />
              <NativeSelect label="Category" required value={form.categoryId} onChange={(val) => setField('categoryId', val)}
                placeholder="Select category…" error={errors.categoryId}
                options={categories.filter((c) => c.isActive || String(c.id) === form.categoryId).map((c) => ({ value: c.id, label: c.name }))} />
              <Input label="Paid To" required value={form.payee} maxLength={150} placeholder="Vendor / person"
                onChange={(e) => setField('payee', e.target.value)} error={errors.payee} />
              <Input label="Description" value={form.description} maxLength={500}
                onChange={(e) => setField('description', e.target.value)} error={errors.description} />
              <Input label="Amount (₹, before GST)" required type="number" value={form.amount}
                onChange={(e) => { setField('amount', e.target.value); setErrors((p) => ({ ...p, taxAmount: undefined })); }} error={errors.amount} />
              <Input label="GST on bill (₹)" type="number" value={form.taxAmount} error={errors.taxAmount}
                onChange={(e) => setField('taxAmount', e.target.value)} helperText="Leave blank if no GST was charged (max 28% of amount)" />
              <NativeSelect label="Payment Mode" required value={form.paymentMode} error={errors.paymentMode}
                onChange={(val) => setField('paymentMode', val)} options={PAYMENT_MODES} />
              <Input label="Reference / UTR" value={form.referenceNo} maxLength={100} error={errors.referenceNo}
                onChange={(e) => setField('referenceNo', e.target.value)} />
              <Input label="Vendor Bill No." value={form.billNumber} maxLength={100} error={errors.billNumber}
                onChange={(e) => setField('billNumber', e.target.value)} />
              {employees.length > 0 && (
                <NativeSelect label="Employee (optional)" value={form.employeeId} onChange={(val) => setField('employeeId', val)}
                  error={errors.employeeId}
                  placeholder="Not linked" options={employees.map((emp) => ({ value: emp.id, label: `${emp.name} (${emp.employeeCode})` }))} />
              )}
            </div>
            <TextArea label="Notes" value={form.notes} onChange={(val) => setField('notes', val)} maxLength={1000} error={errors.notes} />
          </form>
        </Modal>

        <ConfirmDialog />
      </div>
    </Layout>
  );
}
